import { zodResolver } from "@hookform/resolvers/zod";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import type { AdditionalJobDetailsData, AdditionalJobDetailsSection } from "@stryker/core/types/sections";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import validator from "validator";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const additionalJobDetailsSchema = z.object({
  unit: z.string(),
  line_id: z.string().nullable(),
  salesman: z
    .string()
    .trim()
    .transform((val) => (val === "" ? undefined : val))
    .optional()
    .superRefine((val, ctx) => {
      if (val !== undefined && !validator.isEmail(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid email address",
        });
      }
    }),
});

type AdditionalJobDetailsSchema = z.infer<typeof additionalJobDetailsSchema>;

const cardName = "additionalJobDetails";
type AdditionalJobDetailsProps = {
  savedAdditionalJobDetails: AdditionalJobDetailsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setAdditionalJobDetails: (payload: { data?: AdditionalJobDetailsData; status: ExpandStatus }) => void;
};
export function AdditionalJobDetails({
  savedAdditionalJobDetails,
  toggleExpandableSection,
  setAdditionalJobDetails,
}: AdditionalJobDetailsProps) {
  const [addMore, setAddMore] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdditionalJobDetailsSchema>({
    resolver: zodResolver(additionalJobDetailsSchema),
    values: {
      unit: savedAdditionalJobDetails.data.unit,
      line_id: savedAdditionalJobDetails.data.line_id,
      salesman: savedAdditionalJobDetails.data.salesman || undefined,
    },
  });

  useEffect(() => {
    if (!addMore && savedAdditionalJobDetails.isDataFilled) {
      setAddMore(true);
    }
  }, [savedAdditionalJobDetails]);

  const handleSaveAdditionalJobDetails = (data: AdditionalJobDetailsSchema) => {
    setAdditionalJobDetails({
      data,
      status: "done",
    });
  };

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedAdditionalJobDetails.status}
      title="Additional Job Details"
    >
      {addMore ? (
        <form onSubmit={handleSubmit(handleSaveAdditionalJobDetails)} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Unit"
              type="text"
              error={errors.unit?.message}
              {...register("unit")}
            />
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Line id"
              error={errors.line_id?.message}
              {...register("line_id")}
            />
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Salesman Email"
              error={errors.salesman?.message}
              {...register("salesman")}
            />
          </div>
          <Button className="self-end" type="submit">
            Save
          </Button>
        </form>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Typography variant="text-xl" weight="semibold">
            Do you wish to provide additional job details such as Salesman Email, Unit, and line ID?
          </Typography>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAdditionalJobDetails({ status: "done" })}>
              No
            </Button>
            <Button onClick={() => setAddMore(true)}>Yes</Button>
          </div>
        </div>
      )}
    </ExpandableSection>
  );
}
